import { format } from "date-fns";
import { List, Record } from "immutable";

import { defaultValues } from "@config/defaultValues";

import {
  ContractCancellationReasonsState,
  ContractCancellationReasonsStateDto,
} from "@typings/contractCancellation";
import { CLEANING_FREQUENCY } from "@typings/booking";
import { PaymentAlias } from "@typings/payments";
import {
  AccountBalance,
  BasicInfo,
  CompanyInfo,
  CompanyInfoDTO,
  MoneyBalance,
  TotalBalance,
  Referral,
  ReviewInfo,
} from "@typings/profile";
import { ProfileContract } from "@typings/profileContracts";
import { Subscription, Subscriptions } from "@typings/subscription";
import { VAT_INFO } from "@typings/globals";

import { stringifyFalsies } from "@services/StringifyFalsies";
import { DATE_FORMAT } from "@services/DateFormatter";

export const referralInitialState = {
  referralCode: defaultValues.profile.referral.referralCode,
  referralUrl: defaultValues.profile.referral.referralUrl,
  shareFacebookUrl: defaultValues.profile.referral.shareFacebookUrl,
  shareTwitterUrl: defaultValues.profile.referral.shareTwitterUrl,
};

export const referralFactory = (input: Referral): Record<Referral> =>
  Record<Referral>(referralInitialState)(input);

const totalBalanceInitialState: TotalBalance = {
  balance: defaultValues.profile.accountBalance.totalBalance.balance,
  currency: defaultValues.profile.accountBalance.totalBalance.currency,
};

const moneyBalanceInitialState: MoneyBalance = {
  balance: defaultValues.profile.accountBalance.moneyBalance.balance,
  currency: defaultValues.profile.accountBalance.moneyBalance.currency,
};

export const accountBalanceInitialState: AccountBalance = {
  totalBalance: Record<TotalBalance>(totalBalanceInitialState)(),
  moneyBalance: Record<MoneyBalance>(moneyBalanceInitialState)(),
  durationBalance: defaultValues.profile.accountBalance.durationBalance,
};

export const accountBalanceFactory = (
  input: AccountBalance,
): Record<AccountBalance> => {
  const inputWithMoneyBalanceAsRecord: AccountBalance = {
    ...input,
    totalBalance: Record<TotalBalance>(totalBalanceInitialState)(
      input.totalBalance,
    ),
    moneyBalance: Record<MoneyBalance>(moneyBalanceInitialState)(
      input.moneyBalance,
    ),
  };
  return Record<AccountBalance>(accountBalanceInitialState)(
    inputWithMoneyBalanceAsRecord,
  );
};

export const basicInfoInitialState = {
  firstName: defaultValues.profile.basicInfo.firstName,
  lastName: defaultValues.profile.basicInfo.lastName,
  email: defaultValues.profile.basicInfo.email,
  dateOfBirth: defaultValues.profile.basicInfo.dateOfBirth,
  homePhone: defaultValues.profile.basicInfo.homePhone,
  mobilePhone: defaultValues.profile.basicInfo.mobilePhone,
  oAuthProvider: defaultValues.profile.basicInfo.oAuthProvider,
  serviceVoucher: defaultValues.profile.basicInfo.serviceVoucher,
};

export const basicInfoFactory = (input: BasicInfo): Record<BasicInfo> =>
  Record<BasicInfo>(basicInfoInitialState)({
    ...input,
    dateOfBirth: input.dateOfBirth
      ? format(
          new Date(stringifyFalsies(input.dateOfBirth)),
          DATE_FORMAT.DAY_PICKER_INPUT,
        )
      : null,
  });

export const paymentAliasInitialState: PaymentAlias = {
  type: defaultValues.profile.paymentAliases.type,
  maskedNumber: defaultValues.profile.paymentAliases.maskedNumber,
  expirationDate: defaultValues.profile.paymentAliases.expirationDate,
  paymentMethodStatus: defaultValues.profile.paymentAliases.paymentMethodStatus,
  uuid: defaultValues.profile.paymentAliases.uuid,
  default: defaultValues.profile.paymentAliases.default,
};

const paymentAliasFactory = (
  paymentAlias: PaymentAlias,
): Record<PaymentAlias> => Record(paymentAliasInitialState)(paymentAlias);

export const paymentAliasesFactory = (
  paymentAliases: PaymentAlias[],
): List<Record<PaymentAlias>> => List(paymentAliases.map(paymentAliasFactory));

export const deletePaymentAliasFactory = (
  paymentAliases: List<Record<PaymentAlias>>,
  paymentAliasUuid: string,
): List<Record<PaymentAlias>> =>
  paymentAliases.filter(
    paymentAlias => paymentAlias.get("uuid") !== paymentAliasUuid,
  );

export const defaultPaymentAliasFactory = (
  paymentAliases: List<Record<PaymentAlias>>,
  paymentAliasUuid: string,
): List<Record<PaymentAlias>> =>
  paymentAliases.map(paymentAlias => {
    if (
      !paymentAlias.get("default") &&
      paymentAlias.get("uuid") === paymentAliasUuid
    ) {
      return paymentAlias.set("default", true);
    }
    return paymentAlias.set("default", false);
  });

const initialSubscription: Subscription = {
  uuid: "",
  name: "",
  freeCancellationsUsed: 0,
  freeCancellationsTotal: 0,
  startDate: "",
  endDate: "",
  pricePlanUuid: "",
  usePriceForOnceCleaning: false,
  frequency: CLEANING_FREQUENCY.WEEKLY,
  currency: "",
  pricePerHour: "0",
  serviceLocationId: 0,
};

export const clientSubscriptionsFactory = (
  subscriptions: Subscriptions,
): List<Record<Subscription>> =>
  List(
    subscriptions.map(subscription =>
      Record(initialSubscription)(subscription),
    ),
  );

const initialContract: ProfileContract = {
  address: {
    city: "",
    postalCode: "",
    streetAndNumber: "",
  },
  contractUuid: "",
  hasSubscription: false,
  nextCleaningDate: "",
  nextCleaningDay: "",
  canUsePricePlans: false,
  subscription: {
    pricePlanName: "",
    startDate: "",
    endDate: "",
    hasCommitment: false,
  },
  pendingSubscription: null,
  cancellable: false,
  pendingForCancellation: false,
  pricePerHourVatInfo: VAT_INFO.NO_INFO,
  cleaningChecklist: {
    canBeFilled: false,
    isCreated: false,
  },
  hasOnboardingCompleted: false,
  extraServices: [],
  frequency: CLEANING_FREQUENCY.WEEKLY,
  duration: 0,
  hasSpecialInstructions: false,
};

export const clientContractsFactory = (
  contracts: ProfileContract[],
): List<Record<ProfileContract>> =>
  List(contracts.map(contract => Record(initialContract)(contract)));

export const companyInfoInitialState = {
  basicInfo: {
    email: defaultValues.profile.companyInfo.basicInfo.email,
  },
  company: {
    name: defaultValues.profile.companyInfo.company.name,
    email: defaultValues.profile.companyInfo.company.email,
    phone: defaultValues.profile.companyInfo.company.phone,
  },
  contactPerson: {
    email: defaultValues.profile.companyInfo.contactPerson.email,
    firstName: defaultValues.profile.companyInfo.contactPerson.firstName,
    lastName: defaultValues.profile.companyInfo.contactPerson.lastName,
    phone: defaultValues.profile.companyInfo.contactPerson.phone,
  },
};

export const companyInfoFactory = (
  input: CompanyInfoDTO,
): Record<CompanyInfo> =>
  Record(companyInfoInitialState)({
    basicInfo: {
      email: input.basicInfo.email || "",
    },
    company: {
      email: input.company?.email || "",
      phone: input.company?.phone || "",
      name: input.company?.name || "",
    },
    contactPerson: {
      email: input.contactPerson?.email || "",
      phone: input.contactPerson?.phone || "",
      firstName: input.contactPerson?.firstName || "",
      lastName: input.contactPerson?.lastName || "",
    },
  });

export const contractCancellationReasonsInitial: ContractCancellationReasonsState =
  {
    changeRequestRelativeUrl: "",
    reasons: {
      description: "",
      heading: "",
      reasons: [
        {
          commentField: false,
          finishType: [],
          hasRelocationFields: false,
          name: "",
          subReasons: null,
          uuid: "",
        },
      ],
    },
  };

export const contractCancellationReasonsFactory = (
  input: ContractCancellationReasonsStateDto,
): Record<ContractCancellationReasonsState> =>
  Record(contractCancellationReasonsInitial)({
    changeRequestRelativeUrl: input.changeRequestRelativeUrl,
    reasons: input.reasons,
  });

export const reviewInfoInitialState: ReviewInfo = {
  allowToMakeReview: false,
  googleMapsReviewLink: null,
};
